/**
 * Putting a GPS position on a hand-drawn map.
 *
 * A festival site map is an illustration, not a projection. It is stretched
 * where the artist wanted room, compressed where they did not, and rotated to
 * fit the page. No affine transform lands a "you are here" dot correctly on
 * one, and no map library helps, because there is nothing to georeference
 * against.
 *
 * The pilot solves this with a thin-plate spline fit against eight surveyed
 * control points — walk to a stage, record the GPS reading, note where that
 * stage is drawn — and it works: the dot passes exactly through every control
 * point, with roughly 12 m of error elsewhere.
 *
 * The pilot bakes the FITTED WEIGHTS into its source, so a new map means
 * someone re-running a fit by hand. Here the fit happens from control points
 * stored per edition, so a new map is a data change.
 */

export interface ControlPoint {
  readonly lat: number;
  readonly lng: number;
  /** Position on the map image, as 0..1 fractions. */
  readonly x: number;
  readonly y: number;
}

export interface ImagePoint {
  readonly x: number;
  readonly y: number;
}

/**
 * Coordinates are scaled before fitting. Degrees of latitude are tiny numbers
 * across a festival site (~0.005), and the spline's radial term squares them;
 * without scaling the linear system is badly conditioned and the solution is
 * numerical noise.
 */
const SCALE = 1000;

/** The thin-plate spline radial basis, U(r) = r² ln r. */
export function tpsBasis(r: number): number {
  return r === 0 ? 0 : r * r * Math.log(r);
}

export interface TpsFit {
  readonly controls: readonly ControlPoint[];
  /** Radial weights, one per control point, for x and y. */
  readonly wx: readonly number[];
  readonly wy: readonly number[];
  /** Affine part [constant, lng coefficient, lat coefficient]. */
  readonly ax: readonly [number, number, number];
  readonly ay: readonly [number, number, number];
}

/** Gaussian elimination with partial pivoting. */
function solve(matrix: number[][], rhs: number[]): number[] {
  const n = rhs.length;
  const a = matrix.map((row, i) => [...row, rhs[i]!]);

  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(a[row]![col]!) > Math.abs(a[pivot]![col]!)) pivot = row;
    }
    if (Math.abs(a[pivot]![col]!) < 1e-12) {
      throw new Error(
        'control points are degenerate — they are collinear or duplicated, ' +
          'so no unique warp exists. Survey points spread across the site.',
      );
    }
    [a[col], a[pivot]] = [a[pivot]!, a[col]!];

    for (let row = 0; row < n; row++) {
      if (row === col) continue;
      const factor = a[row]![col]! / a[col]![col]!;
      if (factor === 0) continue;
      for (let k = col; k <= n; k++) a[row]![k]! -= factor * a[col]![k]!;
    }
  }
  return a.map((row, i) => row[n]! / row[i]!);
}

/**
 * Fit a thin-plate spline mapping GPS coordinates onto image fractions.
 *
 * Needs at least three non-collinear control points; the pilot uses eight,
 * which is comfortable.
 */
export function fitTps(controls: readonly ControlPoint[]): TpsFit {
  const n = controls.length;
  if (n < 3) throw new Error(`a thin-plate spline needs at least 3 control points, got ${n}`);

  const px = controls.map((c) => c.lng * SCALE);
  const py = controls.map((c) => c.lat * SCALE);

  // [ K  P ] [w]   [v]
  // [ Pᵀ 0 ] [a] = [0]
  const size = n + 3;
  const matrix: number[][] = Array.from({ length: size }, () => new Array<number>(size).fill(0));

  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const dx = px[i]! - px[j]!;
      const dy = py[i]! - py[j]!;
      matrix[i]![j] = tpsBasis(Math.hypot(dx, dy));
    }
    matrix[i]![n] = 1;
    matrix[i]![n + 1] = px[i]!;
    matrix[i]![n + 2] = py[i]!;

    matrix[n]![i] = 1;
    matrix[n + 1]![i] = px[i]!;
    matrix[n + 2]![i] = py[i]!;
  }

  const rhsX = [...controls.map((c) => c.x), 0, 0, 0];
  const rhsY = [...controls.map((c) => c.y), 0, 0, 0];
  const sx = solve(matrix.map((r) => [...r]), rhsX);
  const sy = solve(matrix.map((r) => [...r]), rhsY);

  return {
    controls: [...controls],
    wx: sx.slice(0, n),
    wy: sy.slice(0, n),
    ax: [sx[n]!, sx[n + 1]!, sx[n + 2]!],
    ay: [sy[n]!, sy[n + 1]!, sy[n + 2]!],
  };
}

/** Warp a GPS position onto the map image. Exact at every control point. */
export function geoToImage(fit: TpsFit, lat: number, lng: number): ImagePoint {
  const px = lng * SCALE;
  const py = lat * SCALE;

  let x = fit.ax[0] + fit.ax[1] * px + fit.ax[2] * py;
  let y = fit.ay[0] + fit.ay[1] * px + fit.ay[2] * py;

  for (let i = 0; i < fit.controls.length; i++) {
    const c = fit.controls[i]!;
    const u = tpsBasis(Math.hypot(px - c.lng * SCALE, py - c.lat * SCALE));
    x += fit.wx[i]! * u;
    y += fit.wy[i]! * u;
  }
  return { x, y };
}

/** Is a warped point actually on the image? */
export function isOnMap(p: ImagePoint, margin = 0.02): boolean {
  return p.x >= -margin && p.x <= 1 + margin && p.y >= -margin && p.y <= 1 + margin;
}

/**
 * The image-space direction of true north at a position.
 *
 * The map is rotated and stretched, so "up" on the image is not north. The
 * compass cone has to be rotated by this, or it points confidently the wrong
 * way.
 */
export function northOnImage(fit: TpsFit, lat: number, lng: number): { dx: number; dy: number } {
  const e = 1e-5; // ~1 m
  const here = geoToImage(fit, lat, lng);
  const north = geoToImage(fit, lat + e, lng);
  const dx = north.x - here.x;
  const dy = north.y - here.y;
  const len = Math.hypot(dx, dy) || 1;
  return { dx: dx / len, dy: dy / len };
}

/** Great-circle distance in metres. */
export function haversineMetres(
  aLat: number, aLng: number, bLat: number, bLng: number,
): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}
